import Loading from 'common/components/loading/loading';
import Tile from 'features/tiles/tile';
import { FC, useMemo } from 'react';
import {
  useGetConversionCountQuery,
  useGetCountRevenueQuery,
  useGetTotalAdCostsQuery,
  useGetTotalRevenueQuery,
  useGetVisitorCountQuery
} from 'app/apis/queryApi';
import { useSelector } from 'react-redux';
import { RootState } from 'app/store';
import moment from 'moment';
import { getDateRangeAsObjects } from 'features/date-range-selection/dateRangeSlice';
import { toChartByDate } from './dashboardSummaryChartDataFormatter';
import ChartBridge from 'features/charts/strategies/chart-bridge';
import { selectCurrencyCode } from 'features/settings/settingsSlice';

const DashboardChart: FC = () => {
  const dateRange = useSelector(getDateRangeAsObjects);
  const { siteId } = useSelector((state: RootState) => state.selectedSite);
  const selectedCurrencyCode = useSelector(selectCurrencyCode);

  const {
    data: visitorData,
    isLoading: visitorIsLoading,
    isFetching: visitorIsFetching
  } = useGetVisitorCountQuery({
    siteId,
    startDate: moment(dateRange.startDate).format('DD/MM/YYYY'),
    endDate: moment(dateRange.endDate).format('DD/MM/YYYY'),
    currencyCode: selectedCurrencyCode
  });

  const {
    data: webConversionData,
    isLoading: webConversionIsLoading,
    isFetching: webConversionIsFetching
  } = useGetConversionCountQuery({
    siteId,
    conversionType: 'Web',
    startDate: moment(dateRange.startDate).format('DD/MM/YYYY'),
    endDate: moment(dateRange.endDate).format('DD/MM/YYYY')
  });

  const {
    data: inboundCallsConversionData,
    isLoading: inboundCallsConversionIsLoading,
    isFetching: inboundCallsConversionIsFetching
  } = useGetConversionCountQuery({
    siteId,
    conversionType: 'InboundCall',
    startDate: moment(dateRange.startDate).format('DD/MM/YYYY'),
    endDate: moment(dateRange.endDate).format('DD/MM/YYYY')
  });

  const {
    data: adCostData,
    isLoading: adCostIsLoading,
    isFetching: adCostIsFetching
  } = useGetTotalAdCostsQuery({
    siteId,
    startDate: moment(dateRange.startDate).format('DD/MM/YYYY'),
    endDate: moment(dateRange.endDate).format('DD/MM/YYYY'),
    currencyCode: selectedCurrencyCode
  });

  const { data: revenueData, isFetching: revenueIsFetching } =
    useGetTotalRevenueQuery({
      siteId,
      startDate: moment(dateRange.startDate).format('DD/MM/YYYY'),
      endDate: moment(dateRange.endDate).format('DD/MM/YYYY'),
      currencyCode: selectedCurrencyCode
    });

  const { data: salesData, isFetching: salesIsFetching } =
    useGetCountRevenueQuery({
      siteId,
      startDate: moment(dateRange.startDate).format('DD/MM/YYYY'),
      endDate: moment(dateRange.endDate).format('DD/MM/YYYY'),
      currencyCode: selectedCurrencyCode
    });
  const isChartDataLoaded =
    visitorData !== undefined &&
    webConversionData !== undefined &&
    inboundCallsConversionData !== undefined &&
    adCostData !== undefined &&
    revenueData !== undefined &&
    salesData !== undefined &&
    !visitorIsLoading &&
    !webConversionIsLoading &&
    !inboundCallsConversionIsLoading &&
    !adCostIsLoading &&
    !visitorIsFetching &&
    !webConversionIsFetching &&
    !inboundCallsConversionIsFetching &&
    !adCostIsFetching &&
    !revenueIsFetching &&
    !salesIsFetching;

  const summaryChartData = useMemo(() => {
    if (isChartDataLoaded) {
      return toChartByDate(
        visitorData,
        webConversionData,
        inboundCallsConversionData,
        adCostData,
        revenueData,
        salesData
      );
    }
  }, [
    visitorData,
    webConversionData,
    inboundCallsConversionData,
    isChartDataLoaded,
    adCostData,
    revenueData,
    salesData
  ]);
  return (
    <>
      <Tile className="col-span-12">
        <div className="p-2.5 pb-14 h-92">
          {isChartDataLoaded && summaryChartData ? (
            <ChartBridge chart={summaryChartData} />
          ) : (
            <Loading />
          )}
        </div>
      </Tile>
    </>
  );
};

export default DashboardChart;
